<script>
  import { onMount } from 'svelte';

  import Button from '@components/Button.svelte';
  import Modal from '@components/Modal.svelte';
  import SimpleAvatar from '@app-components/modal/create_account/SimpleAvatar.svelte';
  import UserAjax from '@ajax/user';

  import Track from '@utils/track';
  import Log from '@utils/log';
  import { without } from 'lodash-es';
  import CreateAccountModal from '@app-components/modal/CreateAccountModal.svelte';
  import AfterCreateInfo from '@app-components/modal/create_account/AfterCreateInfo.svelte';

  import { getProfileStore } from '@stores/profile';
  import { getCurrentUserStore } from '@stores/current_user';
  import { createEventDispatcher } from 'svelte';
  const dispatch = createEventDispatcher();

  let followCountStore = getProfileStore('followCountStore');

  export let user;
  export let afterUnfollow;
  export let afterFollow;
  export let profileUser = user;
  export let size = 'normal';
  export let mobile = false;
  export let context = null;
  export let width = null;
  export let autoFollow = false;
  export let type = 'button';

  export let colorScheme;

  const widthStyle = width ? `width: ${width};` : '';

  let followLoading = false;

  let showInfoStepsModal = false;
  let showCreateAccountModal = false;
  let currentUser = getCurrentUserStore('default');

  $: isFollowing = currentUser?.following_ids?.includes(user.id);
  $: showFollowBack = currentUser?.follower_ids?.includes(user.id);
  $: colorSelected = colorScheme && !isFollowing ? colorScheme : isFollowing ? 'gray' : 'purple';

  let showUnfollowModal = false;

  let follow = async function () {
    showInfoStepsModal = false;
    followLoading = true;

    await UserAjax.follow(user);
    await updateFollowCounts();

    Track.userFollowed({ currentUser, user, context, page: location.pathname });
    currentUser.set({ ...currentUser, following_ids: currentUser.following_ids.concat(user.id) });

    followLoading = false;

    if (afterFollow) {
      afterFollow(user);
    }
  };

  let followButtonClicked = async function () {
    dispatch('follow-botton-clicked', {
      path: location.pathname,
      action: currentUser && currentUser.id ? (isFollowing ? 'to-unfollow' : 'to-follow') : 'logged-out',
    });
    if (currentUser && currentUser.id) {
      if (isFollowing) {
        showUnfollowModal = true;
        return;
      }

      follow();
    } else {
      showCreateAccountModal = true;
    }
  };

  let logUserCreatedThroughFollow = function (user) {
    try {
      Log.info(`New Signup - via Follow Button | ${user.name} (${user.email}) | location: ${context}`);
    } catch {}
  };

  let unfollow = async function () {
    followLoading = true;
    await UserAjax.unfollow(user);
    await updateFollowCounts();

    followLoading = false;
    showUnfollowModal = false;
    currentUser.set({ ...currentUser, following_ids: without(currentUser.following_ids, user.id) });
    Track.userUnfollowed({ currentUser, user, context, page: location.pathname });
    if (afterUnfollow) {
      afterUnfollow(user);
    }
  };

  let updateFollowCounts = async function () {
    if (!followCountStore.isInitialized) {
      return;
    }

    let followCounts = await UserAjax.follow_counts(profileUser);

    followCountStore.set({
      followerCount: followCounts.follower || 0,
      followingCount: followCounts.following || 0,
    });
  };

  onMount(() => {
    let sub = currentUser.subscribe((x) => (currentUser = x));
    if (autoFollow && !isFollowing) {
      follow();
    }
    return () => {
      sub();
    };
  });
</script>

<style style="text/scss" lang="scss">
  @import '../vendor/design-system.scss';
  .unfollow-header {
    margin-top: 15px;
    @include typeface-h2;
    text-align: center;
  }

  .unfollow-subheader {
    font-size: 16px;
    line-height: 24px;
    color: $neutral-gray;
    text-align: center;
  }

  .flex-item {
    display: flex;
  }

  .flex-wrapper {
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  .unfollow-buttons {
    width: 180px;
  }

  .user-follow-text {
    display: inline-flex;
    color: black;
    cursor: pointer;

    &:hover {
      opacity: 0.7;
    }
  }

  * :global(.bn-button.btn-small) {
    line-height: 14px !important;
  }

  * :global(.modal__container.show-info-steps) {
    min-width: 780px;
    max-width: 780px;
    min-height: 684px;
    max-height: 684px;

    padding-bottom: 0px;
    margin-bottom: 0px;
    background: linear-gradient(343.65deg, #eae4ff -0.74%, #ffffff 72.21%),
      linear-gradient(180deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
    border-radius: 20px;
  }

  :global(.mobile .modal__overlay .modal__container.show-info-steps) {
    min-width: inherit !important;
    max-width: inherit !important;
    min-height: inherit !important;
    max-height: inherit !important;
    padding: 0px !important;
    margin-top: 30px !important;
  }

  :global(.mobile .modal__overlay .modal__container.show-info-steps .modal__content) {
    margin-top: 0px;
    margin-bottom: 0px;
  }
</style>

{#if showUnfollowModal}
  <div class="svelte-modal">
    <Modal
      style="border-radius: 16px; max-width: 550px"
      mobile={mobile}
      showCloseButton={false}
      on:close={() => {
        showUnfollowModal = false;
      }}
      show={showUnfollowModal}>
      <div slot="content">
        <div class="flex-item flex-wrapper">
          <div class="flex-item">
            <SimpleAvatar width={80} src={user.avatar_url} user={user} mobile={mobile} />
          </div>
          <div class="flex-item">
            <div>
              <h2 class="unfollow-header">Unfollow {user.name}?</h2>
              <p class="unfollow-subheader">Do you want to unfollow {user.name}?</p>
            </div>
          </div>
          <div class="flex-item" class:mobile={mobile}>
            <form class="unfollow-buttons">
              <Button
                loading={followLoading}
                text="Unfollow"
                style="max-width: 230px; margin-left: auto; display: block; margin-right: auto; margin-top: 35px;"
                colorScheme={'red'}
                on:click={unfollow} />

              <Button
                text="Cancel"
                style="max-width: 230px; margin-left: auto; display: block; margin-right: auto; margin-top: 24px; border: none"
                colorScheme={'white'}
                on:click={() => {
                  showUnfollowModal = false;
                }} />
            </form>
          </div>
        </div>
      </div>
    </Modal>
  </div>
{/if}

{#if showInfoStepsModal || showCreateAccountModal}
  <div class="svelte-modal">
    {#if showInfoStepsModal}
      <Modal
        on:close={follow}
        show={showInfoStepsModal}
        mobile={mobile}
        showCloseButton={false}
        closeOnOverlayClick={false}
        fadeInDuration={0}
        containerClass={'show-info-steps'}>
        <div slot="content" style="text-align: center;">
          <div>
            <AfterCreateInfo
              mobile={mobile}
              on:finished={(_e) => {
                showInfoStepsModal = false;
              }} />
          </div>
        </div>
      </Modal>
    {:else if showCreateAccountModal}
      <CreateAccountModal
        msgStyle="font-size: 20px;"
        eventDetails={{trigger_event: 'follow_button', related_user_id: profileUser?.id}}
        mobile={mobile}
        successCallback={(user) => {
          if (currentUser.id != profileUser.id) {
            follow();
          }

          if (user.id && user.api_token && !user.is_new) {
            return;
          }

          logUserCreatedThroughFollow(user);
          showInfoStepsModal = true;
          showCreateAccountModal = false;
        }}
        beforeDataSend={(data) => {
          data['sign_up_source'] = {
            source: 'follow',
            location: context,
          };
          return data;
        }}
        showPassword={true}
        saveButtonFlow={true}
        buttonText="Create account"
        bind:showModal={showCreateAccountModal} />
    {/if}
  </div>
{/if}

{#if user && user.id != currentUser.id && !currentUser.demo_user}
  {#if type == 'button'}
    <Button
      size={size}
      colorScheme={colorSelected}
      on:click={followButtonClicked}
      shadow={false}
      style={size == 'small' ? `line-height: 14px; ${widthStyle}` : `${widthStyle}`}>
      {#if followLoading}
        <i class="fa fa-spinner fa-spin" />
      {:else if isFollowing}
        <span style="margin-right: 8px">Following</span>
        {#if context != 'notification_menu'}
          <img src="/images/follow/checkmark.svg" alt="checkmark" style="color: black" />
        {/if}
      {:else if showFollowBack && context != 'notification_menu'}
        <span style="white-space: nowrap">Follow back</span>
      {:else}
        <span>Follow</span>
      {/if}
    </Button>
  {:else if type == 'text'}
    <span class="user-follow-text" on:click={followButtonClicked}>
      {#if followLoading}
        <i class="fa fa-spinner fa-spin" />
      {:else if isFollowing}
        <span style="margin-right: 8px">Following</span>
        {#if context != 'notification_menu'}
          <img src="/images/follow/checkmark.svg" alt="checkmark" style="color: black" />
        {/if}
      {:else if showFollowBack && context != 'notification_menu'}
        <span style="white-space: nowrap">Follow back</span>
      {:else}
        <span>Follow</span>
      {/if}
    </span>
  {/if}
{/if}
